import {useCallback} from 'react';
import useUserLogs from './useUserLogs';

export const RHS_ACTION = {
  DASHBOARD: 'Retail Hardware Status - Dashboard',
  REGION_CHANGE: 'Retail Hardware Status - Region Change',
  STORE_VIEW: 'Retail Hardware Status - Store View',
  DEVICE_TYPE_MODAL_OPEN: 'Retail Hardware Status - Device Type Modal Open',
  HISTORICAL_MODAL_OPEN: 'Retail Hardware Status - Historical Modal Open',
  HISTORICAL_DATE_CHANGE: 'Retail Hardware Status - Historical Date Change',
  HISTORICAL_REGION_CHANGE: 'Retail Hardware Status - Historical Region Change',
  EXPORT: 'Retail Hardware Status - Export',
} as const;

export type DashboardExportOption =
  | 'full'
  | 'inventory_mismatch'
  | 'network_inactive'
  | 'inventory_or_inactive';

export type ExportContext = 'dashboard' | 'historical' | 'store_detail';

export default function useRetailHardwareStatusLogs() {
  const {dispatch} = useUserLogs();

  const logDashboardLand = useCallback(
    (payload: {view: 'global'; region: string; geoCode: string}) => {
      dispatch(RHS_ACTION.DASHBOARD, payload);
    },
    [dispatch]
  );

  const logRegionChange = useCallback(
    (payload: {previousRegion: string; newRegion: string; geoCode: string}) => {
      dispatch(RHS_ACTION.REGION_CHANGE, payload);
    },
    [dispatch]
  );

  const logStoreViewLand = useCallback(
    (payload: {
      siteId: string;
      storeNumber?: string;
      storeName?: string;
      selectedRegionFromNav?: string;
    }) => {
      dispatch(RHS_ACTION.STORE_VIEW, payload);
    },
    [dispatch]
  );

  const logDeviceTypeModalOpen = useCallback(
    (payload: {
      siteId: string;
      storeNumber?: string;
      storeName?: string;
      selectedRegionFromNav?: string;
      deviceTypeClicked: string;
    }) => {
      dispatch(RHS_ACTION.DEVICE_TYPE_MODAL_OPEN, payload);
    },
    [dispatch]
  );

  const logHistoricalModalOpen = useCallback(
    (payload: {
      dashboardRegion: string;
      initialHistoricalRegion: string;
      initialHistoricalDate: string;
      geoCode: string;
    }) => {
      dispatch(RHS_ACTION.HISTORICAL_MODAL_OPEN, payload);
    },
    [dispatch]
  );

  const logHistoricalDateChange = useCallback(
    (payload: {historicalDate: string; historicalRegion: string; geoCode: string}) => {
      dispatch(RHS_ACTION.HISTORICAL_DATE_CHANGE, payload);
    },
    [dispatch]
  );

  const logHistoricalRegionChange = useCallback(
    (payload: {historicalRegion: string; geoCode: string; historicalDate: string}) => {
      dispatch(RHS_ACTION.HISTORICAL_REGION_CHANGE, payload);
    },
    [dispatch]
  );

  const logExport = useCallback(
    (
      payload:
        | {
            exportContext: 'dashboard';
            exportOption: DashboardExportOption;
            region: string;
            geoCode: string;
            deviceCount: number;
          }
        | {
            exportContext: 'historical';
            historicalRegion: string;
            historicalDate: string;
            geoCode: string;
            rowCount: number;
          }
        | {
            exportContext: 'store_detail';
            siteId: string;
            storeNumber?: string;
            storeName?: string;
            sheetSummary: 'multi_sheet_workbook';
          }
    ) => {
      dispatch(RHS_ACTION.EXPORT, payload);
    },
    [dispatch]
  );

  return {
    logDashboardLand,
    logRegionChange,
    logStoreViewLand,
    logDeviceTypeModalOpen,
    logHistoricalModalOpen,
    logHistoricalDateChange,
    logHistoricalRegionChange,
    logExport,
  };
}
